import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { SeverityLevel } from '@microsoft/applicationinsights-web';

import {
  AttributablePay,
  PayMonthNumberOfReportedViewModel,
  PayReportingTableViewModel,
  PayReportingTableViewModelOld,
  PayrollDetailsFileViewModel,
  PayrollStartPageViewModel,
  PayRow,
} from '@fora-monorepo/employer-my-pages-common/types';
import { getSwedishDateTimeToday } from '@fora-monorepo/date-utils';
import { trackException, trackTrace } from '@fora-monorepo/webapp-analytics';

import { envConfig } from '../../config';
import { mockPayMonthData, mockPayMonthNumberOfReported, mockReportPayroll } from './mocks';
import { createThunkError } from '../utils';
import { mockPayrollStartPage } from './mocks/mockPayrollStartPage';
import { mockPayrollReportCSV } from '../report/mocks';
import { mockPayMonthDataOld } from './mocks/mockPayMonthDataOld';

export const getPayReportData = createAsyncThunk('getPayReportData', async (data: { payMonth: string }, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockPayMonthData.allKinds;
  }

  return axios
    .get(`${envConfig.bffBaseUri}/payroll/${data.payMonth}/data`)
    .then(res => {
      if (res.status === 200) {
        return res.data as PayReportingTableViewModel;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get pay report data', err));
    });
});

export const getPayReportDataOld = createAsyncThunk(
  'getPayReportDataOld',
  async (data: { payMonth: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockPayMonthDataOld.allKinds;
    }

    return axios
      .get(`${envConfig.bffBaseUri}/payroll/${data.payMonth}/data`)
      .then(res => {
        if (res.status === 200) {
          return res.data as PayReportingTableViewModelOld;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get pay report data', err));
      });
  }
);

export const getPayMonthOverviews = createAsyncThunk('getPayMonthOverviews', async (_, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 500));
    return mockPayrollStartPage.default;
  }

  return axios
    .get(`${envConfig.bffBaseUri}/payroll/pay-month-overviews`)
    .then(res => {
      if (res.status === 200) {
        return res.data.payrollStartPage as PayrollStartPageViewModel;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not get pay month', err));
    });
});

export const setEmploymentPayDraft = createAsyncThunk(
  'setEmploymentPayDraft',
  async (
    data: {
      policyNumber: string;
      payAmount: number | null;
      attributablePay?: AttributablePay;
      payMonth: string;
    },
    thunkAPI
  ) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return { timeStamp: getSwedishDateTimeToday() };
    }

    const payload = {
      policyNumber: data.policyNumber,
      payAmount: data.payAmount,
      attributablePay: data.attributablePay,
    };
    return axios
      .post(`${envConfig.bffBaseUri}/payroll/draft/${data.payMonth}/save`, payload)
      .then(res => {
        if (res.status === 200) {
          return { timeStamp: res.data.timeStamp };
        } else {
          // TODO: Error handling upon failure to save draft data #76485
          return thunkAPI.rejectWithValue(createThunkError('Unexpected status code', res.status));
        }
      })
      .catch(err => {
        trackException(err);
        // TODO: Error handling upon failure to save draft data #76485
        thunkAPI.rejectWithValue(createThunkError('Could not save draft data', err));
      });
  }
);

export const reportPayroll = createAsyncThunk(
  'reportPayroll',
  async ({ payroll, payMonth }: { payroll: PayRow[]; payMonth: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      // return thunkAPI.rejectWithValue(createThunkError('Could not get data'));
      return mockReportPayroll.successful;
    }
    return axios
      .post(`${envConfig.bffBaseUri}/payroll/reportPayroll`, { payroll, payMonth })
      .then(res => {
        if (res.status === 200) {
          return res.data;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError('Could not get data', err));
      });
  }
);

export const deleteDrafts = createAsyncThunk('deleteDrafts', async (data: { payMonth: string }, thunkAPI) => {
  if (envConfig.mockDataFrontend) {
    await new Promise(resolve => setTimeout(resolve, 1500));
    return mockReportPayroll.successful;
  }
  return axios
    .delete(`${envConfig.bffBaseUri}/payroll/draft/${data.payMonth}`)
    .then(res => {
      if (res.status === 200) {
        return res.data;
      }
    })
    .catch(err => {
      trackException(err);
      return thunkAPI.rejectWithValue(createThunkError('Could not delete drafts', err));
    });
});

export const getPayrollDetailsCSVFromPayrollData = createAsyncThunk(
  'getPayrollDetailsCSVFromPayrollData',
  async (data: { payRows: PayRow[]; reportedAtTimestamp: string }, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1500));
      return mockPayrollReportCSV;
    } else {
      return axios
        .post(
          `${envConfig.bffBaseUri}/payroll/download/details`,
          { payroll: data.payRows },
          { params: { reportedAtTimestamp: data.reportedAtTimestamp } }
        )
        .then(res => {
          if (res.status === 200) {
            return res.data as PayrollDetailsFileViewModel;
          } else if (res.status === 204) {
            trackTrace(`csv for payroll data: ${JSON.stringify(data)} returned 204 no content`, SeverityLevel.Warning);
            return null;
          }
        })
        .catch(err => {
          trackException(err);
          return thunkAPI.rejectWithValue(
            createThunkError('Could not get csv data from manually entered payroll data', err)
          );
        });
    }
  }
);

export const getNumberOfReportedForPayMonth = createAsyncThunk(
  'getNumberOfReported',
  async (payMonth: string, thunkAPI) => {
    if (envConfig.mockDataFrontend) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return mockPayMonthNumberOfReported;
    }

    return axios
      .get(`${envConfig.bffBaseUri}/payroll/number-of-reported/${payMonth}`)
      .then(res => {
        if (res.status === 200) {
          return res.data as PayMonthNumberOfReportedViewModel;
        }
      })
      .catch(err => {
        trackException(err);
        return thunkAPI.rejectWithValue(createThunkError(`Could not get number of reported for ${payMonth}`, err));
      });
  }
);
